/**
 * Verify Email API Endpoint Schemas
 *
 * POST /api/user/verify-email
 * Verifies OTP code and updates user's email (and optionally name)
 */

import { z } from 'zod';

// Request schema
export const VerifyEmailRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  code: z.string().length(6, 'Code must be 6 digits'),
  // One letter is a name. The more-info step checks the WHOLE name for two
  // characters and sends its first word, so "J Smith" arrived as "J" and a
  // min(2) here refused the email code every time while /api/user/profile had
  // already saved that same "J". Blank stays a refusal: it is no name at all.
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().optional(),
});

export type VerifyEmailRequest = z.infer<typeof VerifyEmailRequestSchema>;

// Profile in response
export const VerifyEmailProfileSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  username: z.string().nullable(),
  userType: z.number().nullable(),
  membershipType: z.number().nullable(),
  hasPhoto: z.number().nullable(),
  trustLevel: z.number().nullable(),
  isPhoneVerified: z.boolean(),
  isEmailVerified: z.boolean(),
  status: z.number().nullable(),
  // String label-format token (e.g. "PDF_4x6") — matches the SHIPPING_FORMAT
  // varchar(15) column and UpdateProfileUserSchema. Was wrongly z.number(),
  // mirroring a since-fixed Number() coercion bug in the endpoint.
  shippingFormat: z.string().max(15).nullable(),
  photos: z
    .object({
      url: z.string(),
      thumbUrl: z.string(),
    })
    .optional(),
  // Coerce so the schema validates both the in-memory Date the endpoint
  // constructs and the ISO string clients receive over the wire.
  updatedDate: z.coerce.date().nullable(),
});

export type VerifyEmailProfile = z.infer<typeof VerifyEmailProfileSchema>;

// Success response
export const VerifyEmailSuccessSchema = z.object({
  success: z.literal(true),
  profile: VerifyEmailProfileSchema,
});

export type VerifyEmailSuccess = z.infer<typeof VerifyEmailSuccessSchema>;

// Error response
export const VerifyEmailErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  /** 'TOO_MANY_ATTEMPTS' when the guess budget is spent; 'EMAIL_HAS_ACCOUNT' with a signInCode. */
  errorCode: z.string().optional(),
  /** With EMAIL_HAS_ACCOUNT: a login code for the email, for the browser to sign in with. */
  signInCode: z.string().optional(),
});

export type VerifyEmailError = z.infer<typeof VerifyEmailErrorSchema>;

// Combined response schema
export const VerifyEmailResponseSchema = z.discriminatedUnion('success', [
  VerifyEmailSuccessSchema,
  VerifyEmailErrorSchema,
]);

export type VerifyEmailResponse = z.infer<typeof VerifyEmailResponseSchema>;
